/**
 * SIRET et SIREN : contrôle, mise en forme, analyse d'une saisie — lot 1.1 (saisie assistée).
 *
 * Module pur, porté du premier SaaS (`lib/entreprises.ts`, testé) : il ne fait aucun appel réseau. Il sert au
 * client (déclencher ou non une recherche), au serveur (borner ce qu'on transmet à l'API de l'État) et au
 * questionnaire de candidature (`candidature/questionnaire.ts` réutilise `siretValide`).
 */

/** SIREN de La Poste : ses établissements ne respectent pas tous la clé de Luhn (somme des chiffres multiple de 5). */
export const SIREN_LA_POSTE = "356000000";

/** Retire espaces (y compris insécables), points et tirets. */
export const nettoieNumero = (valeur: string) => valeur.replace(/[\s.-]/g, "");

/** « 418 166 096 00069 » pour un SIRET, « 418 166 096 » pour un SIREN ; toute autre saisie est rendue telle quelle. */
export function formateSiret(valeur: string): string {
  const v = nettoieNumero(valeur);
  if (/^\d{14}$/.test(v)) return `${v.slice(0, 3)} ${v.slice(3, 6)} ${v.slice(6, 9)} ${v.slice(9)}`;
  if (/^\d{9}$/.test(v)) return `${v.slice(0, 3)} ${v.slice(3, 6)} ${v.slice(6)}`;
  return valeur;
}

function luhn(chiffres: string): boolean {
  let somme = 0;
  for (let i = 0; i < chiffres.length; i++) {
    let n = Number(chiffres[chiffres.length - 1 - i]);
    if (i % 2 === 1) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    somme += n;
  }
  return somme % 10 === 0;
}

export function sirenValide(valeur: string): boolean {
  const v = nettoieNumero(valeur);
  return /^\d{9}$/.test(v) && (v === SIREN_LA_POSTE || luhn(v));
}

/** SIRET : 14 chiffres (espaces, points et tirets tolérés) et clé de Luhn, avec l'exception de La Poste. */
export function siretValide(valeur: string): boolean {
  const v = nettoieNumero(valeur);
  if (!/^\d{14}$/.test(v)) return false;
  if (v.startsWith(SIREN_LA_POSTE)) return luhn(v) || [...v].reduce((a, c) => a + Number(c), 0) % 5 === 0;
  return luhn(v);
}

export type TypeRecherche = { type: "siret"; q: string } | { type: "siren"; q: string } | { type: "nom"; q: string } | { type: "invalide"; message: string } | { type: "incomplet" };

/** Longueur maximale d'une recherche par nom transmise à l'API. */
export const LONGUEUR_MAX_NOM = 120;

/**
 * Nature de la saisie : SIRET (14 chiffres valides), SIREN (9 chiffres valides), nom d'entreprise (3 caractères
 * dont une lettre) ; « incomplet » tant qu'il n'y a pas de quoi chercher, « invalide » si la clé de contrôle est fausse.
 */
export function analyseSaisie(valeur: string): TypeRecherche {
  const brut = valeur.trim();
  const chiffres = nettoieNumero(brut);
  if (/^\d+$/.test(chiffres)) {
    if (chiffres.length === 14) return siretValide(chiffres) ? { type: "siret", q: chiffres } : { type: "invalide", message: "Ce SIRET n'existe pas : vérifiez les 14 chiffres (clé de contrôle incorrecte)." };
    if (chiffres.length === 9) return sirenValide(chiffres) ? { type: "siren", q: chiffres } : { type: "invalide", message: "Ce SIREN n'existe pas : vérifiez les 9 chiffres (clé de contrôle incorrecte)." };
    if (chiffres.length > 14) return { type: "invalide", message: "Un SIRET comporte 14 chiffres." };
    return { type: "incomplet" };
  }
  if (brut.length >= 3 && /\p{L}/u.test(brut)) return { type: "nom", q: brut.replace(/\s+/g, " ").slice(0, LONGUEUR_MAX_NOM) };
  return { type: "incomplet" };
}
